import { ReactComponent as VideoUploadLogo } from '@/assets/video_upload.svg'
import Button from '@/shared/Button'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import get from 'lodash/get'

import FormAutocomplete from '@/shared/FormCreator/FormItem/FormAutocomplete'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Dropzone from 'react-dropzone'

import Spinner from '@/shared/Spinner'
import debounce from '@mui/material/utils/debounce'
import axios from 'axios'
import { connect } from 'react-redux'

const mapStateToProps = ({ loading }) => ({
  isLabelsLoading: loading['courseTemplate/getCourseTemplateLabels'],
})

const stackSx = {
  flexDirection: 'column',
  display: 'flex',
  height: '100%',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
}

// const createChunks = (file, cSize /* cSize should be byte 1024*1 = 1KB */) => {
//   let startPointer = 0
//   const endPointer = file.size
//   const chunks = []
//   while (startPointer < endPointer) {
//     const newStartPointer = startPointer + cSize
//     chunks.push({
//       data: file.slice(startPointer, newStartPointer),
//       start: startPointer,
//       end: min([newStartPointer, file.size]) - 1,
//     })
//     startPointer = newStartPointer
//   }
//   return chunks
// }

// const uploadVideoChunks = async ({ vidUpUrl, videoFile }) => {
//   const chunkSize = 1048576
//   console.log('vid url', vidUpUrl)
//   if (vidUpUrl) {
//     const chunks = createChunks(videoFile, chunkSize)
//     console.log('chunks', chunks)

//     if (!isEmpty(chunks)) {
//       for (let i = 0; i < chunks.length; i += 1) {
//         const { data: chunkData, start, end } = chunks[i]

//         const formData = new FormData()
//         formData.append('data', chunkData)
//         // eslint-disable-next-line no-await-in-loop
//         const { status: chunkStatus } = await axios.put(vidUpUrl, chunkData, {
//           headers: {
//             'Content-Range': `bytes ${start}-${end}/${videoFile.size}`,
//             'Content-Type':
//               'application/octet-streamapplication/octet-streamapplication/octet-stream',
//             // ...(i === chunks.length - 1 ? { 'X-Goog-Meta-Content-Type': videoFile.type } : {}),
//           },
//         })

//         if (chunkStatus !== 200) {
//           console.log('chunk failed upload, abort')
//           return
//         }
//         console.log(`success upload chunk ${i + 1}/${chunks.length}`)

//         // eslint-disable-next-line no-await-in-loop
//         await new Promise(res => {
//           setTimeout(res, 200)
//         })
//       }
//     }
//   }
// }

function getVideoCover(file) {
  return new Promise((resolve, reject) => {
    // load the file to a video player
    const videoPlayer = document.createElement('video')
    videoPlayer.setAttribute('src', URL.createObjectURL(file))
    videoPlayer.load()
    videoPlayer.addEventListener('error', ex => {
      reject(Error('error when loading video file', ex))
    })
    // load metadata of the video to get video duration and dimensions
    videoPlayer.addEventListener('loadedmetadata', () => {
      // seek to user defined timestamp (in seconds) if possible
      const seekTo = videoPlayer.duration * 0.1

      // delay seeking or else 'seeked' event won't fire on Safari
      setTimeout(() => {
        videoPlayer.currentTime = seekTo
      }, 200)
      // extract video thumbnail once seeking is complete
      videoPlayer.addEventListener('seeked', () => {
        // define a canvas to have the same dimension as the video
        const canvas = document.createElement('canvas')
        canvas.width = videoPlayer.videoWidth
        canvas.height = videoPlayer.videoHeight
        // draw the video frame to canvas
        const ctx = canvas.getContext('2d')
        ctx.drawImage(videoPlayer, 0, 0, canvas.width, canvas.height)
        // return the canvas image as a blob
        ctx.canvas.toBlob(
          blob => {
            resolve({ blob, videoLength: videoPlayer.duration })
          },
          'image/jpeg',
          0.75 /* quality */,
        )
      })
    })
  })
}

const VideoUpload = ({
  dispatch,
  isLabelsLoading,
  setNewCategory,
  setCategory,
  onComplete,
  onCancel,
}) => {
  const { t } = useTranslation()

  const [page, setPage] = useState(0)
  const [categorySearch, setCategorySearch] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isUploading, setIsUploading] = useState(false)
  const [imageData, setImageData] = useState()
  const uploadController = useRef()

  const { data: searchResultsData = [], paging } = searchResults

  const setCategorySearchDebounce = debounce(value => {
    setCategorySearch(value)
  }, 400)

  const loadMoreDebounce = debounce(() => {
    setPage(p => p + 1)
  }, 400)

  useEffect(() => {
    dispatch({
      type: 'courseTemplate/getCourseTemplateLabels',
      payload: { searchLabelName: categorySearch, pageIndex: page, pageSize: 10 },
      successCallback: response => {
        const { data: responseData, paging: responsePaging } = response
        const { pageIndex } = responsePaging

        const newData = responseData.map(d => ({ id: d.labelId, label: d.name }))

        setSearchResults(s => ({
          data: pageIndex > 0 ? [...(s.data || []), ...newData] : newData,
          paging: responsePaging,
        }))
      },
    })
  }, [dispatch, categorySearch, page])

  return (
    <Stack py={2}>
      <FormAutocomplete
        inputConfig={{
          freeSolo: true,
          placeholder: t('general.select_add_category'),
          onInputChange: (_, value, reason) => {
            if (['input', 'clear'].includes(reason)) {
              setPage(0)
              setCategorySearchDebounce(value)

              setCategory()
              setNewCategory(value)
            }

            if (reason === 'reset') {
              setCategory(value)
            }
          },
          sx: { mb: 2, width: 280 },
          ListboxProps: {
            role: 'list-box',
            sx: { maxHeight: 200 },
            onScroll: event => {
              const listboxNode = event.currentTarget

              if (
                listboxNode.scrollTop + listboxNode.clientHeight >=
                listboxNode.scrollHeight - 10
              ) {
                const { hasNext } = paging

                if (!isLabelsLoading && hasNext) {
                  loadMoreDebounce()
                }
              }
            },
          },
        }}
        options={searchResultsData}
        disableClearable={true}
        isOptionEqualToValue={(option, value) => option.id === value.id}
      />

      <Dropzone
        accept={{ 'video/*': [] }}
        onDropAccepted={async files => {
          const videoFile = files[0]

          if (videoFile) {
            setIsUploading(true)
            const { blob: imageBlob, videoLength } = await getVideoCover(videoFile, 0)

            dispatch({
              type: 'courseTemplate/getCourseTemplateUpUrl',
              successCallback: async urls => {
                const vidUpUrl = get(urls, 'videoUploadUrl.signedUrl')
                const vidUpId = get(urls, 'videoUploadUrl.signedUrlId')

                const thumbUpUrl = get(urls, 'videoThumbnailUploadUrl.signedUrl')
                const thumbUpId = get(urls, 'videoThumbnailUploadUrl.signedUrlId')
                // uploadVideoChunks({ vidUpUrl, videoFile })
                const vidFormData = new FormData()
                vidFormData.append('file', videoFile)

                const thumbFormData = new FormData()
                const thumbFile = new File([imageBlob], `thumb_${vidUpId}_${thumbUpId}.jpg`)
                thumbFormData.append('file', thumbFile)

                await axios.put(thumbUpUrl, thumbFile)

                uploadController.current?.abort()
                uploadController.current = new AbortController()

                await axios
                  .put(vidUpUrl, videoFile, {
                    headers: { 'Content-Type': videoFile.type },
                    signal: uploadController.current.signal,
                  })
                  .then(() => {
                    onComplete?.({ vidUpId, thumbUpId, videoLength })
                    setImageData(URL.createObjectURL(imageBlob))
                    setIsUploading(false)
                  })
              },
            })
          }
        }}
      >
        {({ getRootProps }) => (
          <Box
            disabled
            sx={{ border: theme => `1px dashed ${theme.palette.secondary_80.main}`, height: 250 }}
          >
            {isUploading || imageData ? (
              <Stack sx={stackSx} spacing={3}>
                {imageData ? (
                  <img src={imageData} style={{ height: '60%', objectFit: 'contain' }} />
                ) : (
                  <Spinner sx={{ flex: 'unset' }} progressProps={{ size: 60 }} />
                )}
                <Button
                  variant="outlined"
                  onClick={() => {
                    uploadController.current?.abort()
                    setImageData()
                    onCancel?.()
                    setIsUploading(false)
                  }}
                >
                  {t('general.cancel')}
                </Button>
              </Stack>
            ) : (
              <Stack
                {...getRootProps()}
                spacing={1}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: theme => theme.palette.grey_80.main },
                  ...stackSx,
                }}
              >
                <VideoUploadLogo style={{ height: '33%', width: 'unset' }} />
                <Typography fontWeight={'bold'} color={theme => theme.palette.secondary_80.main}>
                  {t('general.upload_video_hint')}
                </Typography>
              </Stack>
            )}
          </Box>
        )}
      </Dropzone>
    </Stack>
  )
}

export default connect(mapStateToProps)(VideoUpload)
